import { ZodUnion } from "zod";
import { getTargetObject } from "./accessHelpers.js";
import { getCustomFieldSettings } from "./customSettings.js";
import { parseFields } from "./schemaParser.js";
/**
 * Get the possible string values for a union field in a schema.
 *
 * @param options The options to get the possible values from.
 * @returns The possible values for the specified field.
 */
export function getPossibleValues(options) {
    const parsedOptions = getSelectOptions(options);
    return parsedOptions.map((o) => {
        return o.value;
    });
}
/**
 * Get the options for a union field in a schema.
 *
 * @param options The options to get the select options from.
 * @returns The select options for the specified field.
 */
export function getSelectOptions(options) {
    return options
        .map(({ description, value }) => {
        const label = getOptionLabel(description, value);
        return {
            label,
            value,
            description,
        };
    })
        .filter((o) => {
        return !isOptionHidden(o);
    });
}
/**
 * Gets an object with the options as keys and the values as values.
 *
 * @template S The type of the values in the object.
 * @param options The options to get the object from.
 * @returns The object with the options as keys and the values as values.
 */
export function getUnionObject(options) {
    const parsed = {};
    const optionsArray = options instanceof ZodUnion ? options.options : options;
    optionsArray.forEach((option) => {
        let value;
        let description;
        if ("_def" in option) {
            value = option._def.value;
            description = option._def.description;
        }
        else {
            value = option.value;
            description = option.description;
        }
        const label = getOptionLabel(description, value);
        parsed[label] = value;
    });
    return parsed;
}
/** Values that the library uses to indicate a custom value. */
export const customSelectIndicators = Object.freeze(["Custom", "Other"]);
/**
 * Returns true if the selected value is a custom value.
 *
 * @param selectedValue The value to check
 * @param options The options to check against
 * @returns True if the selected value is a custom value.
 */
export function isCustomValueSelected(selectedValue, options) {
    const possibleValues = getPossibleValues(options);
    if (typeof selectedValue === "undefined" || selectedValue === null)
        return false;
    return (customSelectIndicators.includes(selectedValue) ||
        !possibleValues.includes(selectedValue));
}
/**
 * Returns true if the option should be hidden from a displayed form.
 *
 * @param option The option to check
 * @returns True if the option should be hidden from a displayed form.
 */
export function isOptionHidden(option) {
    if (typeof option.value === "string" && !option.value.trim())
        return true;
    if (!option.description)
        return false;
    const parsed = getCustomFieldSettings(option.description);
    if (!parsed)
        return false;
    return !!parsed.hide;
}
/**
 * Get the label for the given option
 *
 * @param description The description of the option to get the label for (the
 *   .describe content)
 * @param value The value of the field
 * @returns The label for the option or false if no label is specified for the
 *   option.
 */
export function getOptionLabel(description, value) {
    let parsedDescription;
    if (typeof description === "object") {
        parsedDescription = description;
    }
    else {
        parsedDescription = getCustomFieldSettings(description || "");
    }
    if (parsedDescription && parsedDescription.label)
        return parsedDescription.label;
    if (typeof description === "string" && !parsedDescription)
        return description;
    const label = value;
    if (typeof label !== "string") {
        if (typeof value === "boolean") {
            return value ? "Yes" : "No";
        }
        return String(value);
    }
    return label;
}
/**
 * Get the options for a union field in a schema.
 *
 * @param shape The schema shape to get the options from.
 * @param fieldToGet The field in the schema to get the options from.
 * @param omitCustom Skip custom values in the list
 * @returns The options for the specified field.
 * @throws Error If the field is not found in the schema.
 * @throws Error If the field type does not have options.
 */
export function getOptions(shape, fieldToGet, omitCustom = false) {
    const allFields = parseFields(shape, null);
    const { targetObject: fields, fieldName } = getTargetObject(fieldToGet, allFields);
    const field = fields.find((f) => f.field === fieldName);
    if (!field) {
        throw new Error(`Field ${fieldToGet} not found in schema.`);
    }
    if (!("options" in field)) {
        throw new Error(`Field ${fieldToGet} does not have options.`);
    }
    return field.options.filter((v) => !omitCustom || (v.typeName !== "ZodString" && v.value !== "Custom"));
}
//# sourceMappingURL=selectHelpers.js.map